import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import TopBar from "@/components/TopBar";
import VerifiedOrgBadge from "@/components/VerifiedOrgBadge";
import { BRANCH_META } from "@/lib/branches";
import { motion } from "framer-motion";
import { Briefcase, MapPin, ArrowRight, Building2, Sparkles, PlusCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const ROLE_TYPE_LABEL = {
    full_time: "Full-time",
    part_time: "Part-time",
    contract: "Contract",
    internship: "Internship",
};

export default function Careers() {
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    const [roleType, setRoleType] = useState(null);
    const [verifiedOnly, setVerifiedOnly] = useState(false);
    const [loading, setLoading] = useState(false);

    const load = async () => {
        setLoading(true);
        const p = new URLSearchParams();
        if (roleType) p.set("role_type", roleType);
        if (verifiedOnly) p.set("verified_only", "true");
        p.set("limit", "60");
        try {
            const { data } = await api.get(`/jobs?${p}`);
            setItems(data || []);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [roleType, verifiedOnly]);

    return (
        <div className="min-h-screen w-full">
            <TopBar />
            <div className="max-w-5xl mx-auto pt-24 pb-16 px-4 sm:px-8">
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="crystal rounded-3xl p-6 sm:p-8">
                    <div className="flex flex-wrap items-start justify-between gap-4">
                        <div>
                            <p className="chip" style={{ background: BRANCH_META.business.soft, color: BRANCH_META.business.text }}>
                                <Briefcase className="w-3 h-3" /> Careers
                            </p>
                            <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter text-slate-900 mt-2 leading-none">
                                Companies hiring in the Atlas.
                            </h1>
                            <p className="text-slate-600 mt-2 max-w-2xl">
                                Real recruitment posts from organizations and people looking for
                                talent in specific disciplines. Every role links to the org's own
                                application flow — Project Atlas just connects you.
                            </p>
                        </div>
                        {user && (
                            <Link to="/jobs/new" className="arcade-btn" data-variant="primary" data-testid="post-job-btn">
                                <PlusCircle className="w-4 h-4" /> Post a role
                            </Link>
                        )}
                    </div>

                    <div className="mt-6 flex flex-wrap gap-1.5">
                        {[
                            { k: null, label: "All roles" },
                            { k: "full_time", label: "Full-time" },
                            { k: "part_time", label: "Part-time" },
                            { k: "contract", label: "Contract" },
                            { k: "internship", label: "Internship" },
                        ].map((r) => (
                            <button
                                key={r.label}
                                onClick={() => setRoleType(r.k)}
                                data-testid={`career-role-filter-${r.k || "all"}`}
                                className={`chip transition-all ${roleType === r.k ? "bg-slate-900 text-cream" : "bg-white/70 text-slate-700 hover:bg-white"}`}
                            >
                                {r.label}
                            </button>
                        ))}
                        <button
                            onClick={() => setVerifiedOnly((v) => !v)}
                            data-testid="career-verified-toggle"
                            className={`chip transition-all ${verifiedOnly ? "bg-emerald-600 text-white" : "bg-white/70 text-slate-700 hover:bg-white"}`}
                        >
                            <Sparkles className="w-3 h-3" /> Verified orgs only
                        </button>
                    </div>
                </motion.div>

                <div className="mt-6 grid gap-3" data-testid="jobs-grid">
                    {loading && <p className="text-slate-500 text-sm">Loading roles…</p>}
                    {!loading && items.length === 0 && (
                        <div className="crystal rounded-2xl p-8 text-center text-slate-500" data-testid="jobs-empty">
                            <Building2 className="w-8 h-8 mx-auto mb-2 opacity-40" />
                            No roles listed yet. Be the first to post — $5 flat, 30-day listing.
                        </div>
                    )}
                    {items.map((j) => (
                        <Link
                            key={j.id}
                            to={`/jobs/${j.id}`}
                            className="crystal rounded-2xl p-5 hover:shadow-xl transition-all group"
                            data-testid={`job-card-${j.id}`}
                        >
                            <div className="flex items-start justify-between gap-4">
                                <div className="min-w-0">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <span className="chip" style={{ background: BRANCH_META.business.soft, color: BRANCH_META.business.text }}>
                                            <Briefcase className="w-3 h-3" /> {ROLE_TYPE_LABEL[j.role_type] || "Role"}
                                        </span>
                                        {j.location && (
                                            <span className="chip bg-white/70 text-slate-700">
                                                <MapPin className="w-3 h-3" /> {j.location}
                                            </span>
                                        )}
                                    </div>
                                    <h3 className="font-display font-black text-2xl tracking-tight text-slate-900 mt-2 group-hover:text-humanities transition-colors">
                                        {j.title}
                                    </h3>
                                    <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-slate-600">
                                        <Building2 className="w-4 h-4" />
                                        <span className="font-display font-semibold">{j.org_name || j.posted_by_name}</span>
                                        {j.org_verified && <VerifiedOrgBadge />}
                                    </div>
                                    {j.compensation_note && (
                                        <p className="text-sm text-slate-700 mt-1 font-mono">{j.compensation_note}</p>
                                    )}
                                    <p className="text-sm text-slate-600 mt-2 line-clamp-2">{j.description}</p>
                                    <div className="mt-3 flex flex-wrap gap-1.5">
                                        {(j.discipline_slugs || []).slice(0, 4).map((d) => (
                                            <span key={d} className="chip bg-white/70 text-slate-600 text-[11px]">#{d}</span>
                                        ))}
                                    </div>
                                </div>
                                <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-humanities transition-colors flex-shrink-0" />
                            </div>
                        </Link>
                    ))}
                </div>
            </div>
        </div>
    );
}
